import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useMemo } from 'preact/hooks';
import { EmailSendRuleKind, EmailOffsetUnit, EmailEventType } from '@/types/touchPoint';
const UNIT_PLURAL = {
    [EmailOffsetUnit.Minute]: ['minute', 'minutes'],
    [EmailOffsetUnit.Hour]: ['hour', 'hours'],
    [EmailOffsetUnit.Day]: ['day', 'days'],
};
export function SentenceRuleEditor({ value, onChange, lookup, isLoading }) {
    const isScheduled = value.Kind === EmailSendRuleKind.Scheduled;
    const isEvent = value.Kind === EmailSendRuleKind.Event;
    const anchorOptions = useMemo(() => (lookup?.LegalEventTypes ?? []).map(a => ({ key: a.Key, label: a.Label, allowsNegative: a.AllowsNegativeOffset })), [lookup]);
    const activeAnchor = anchorOptions.find(a => a.key === value.EventType) ?? null;
    const negativeAllowed = activeAnchor?.allowsNegative ?? false;
    function setKind(next) {
        if (next === value.Kind)
            return;
        if (next === EmailSendRuleKind.Scheduled) {
            onChange({
                Kind: EmailSendRuleKind.Scheduled,
                SendAtAbsolute: value.SendAtAbsolute ?? defaultScheduledTime(),
                EventType: null,
                OffsetValue: null,
                OffsetUnit: null,
            });
        }
        else {
            onChange({
                Kind: EmailSendRuleKind.Event,
                SendAtAbsolute: null,
                EventType: value.EventType ?? anchorOptions[0]?.key ?? EmailEventType.WebRegistered,
                OffsetValue: value.OffsetValue ?? 0,
                OffsetUnit: value.OffsetUnit ?? EmailOffsetUnit.Day,
            });
        }
    }
    return (_jsxs("div", { className: "space-y-3", children: [_jsx(ModeToggle, { isScheduled: isScheduled, onChange: setKind }), isScheduled && (_jsx(ScheduledSentence, { value: value, onChange: onChange })), isEvent && (_jsx(EventSentence, { value: value, onChange: onChange, anchorOptions: anchorOptions, negativeAllowed: negativeAllowed, isLoading: isLoading }))] }));
}
// ----- Mode toggle ---------------------------------------------------------
function ModeToggle({ isScheduled, onChange, }) {
    return (_jsxs("div", { className: "inline-flex rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-0.5 text-xs", children: [_jsx(ModeButton, { active: isScheduled, onClick: () => onChange(EmailSendRuleKind.Scheduled), children: "On a date" }), _jsx(ModeButton, { active: !isScheduled, onClick: () => onChange(EmailSendRuleKind.Event), children: "Anchored to an event" })] }));
}
function ModeButton({ active, onClick, children, }) {
    return (_jsx("button", { type: "button", onClick: onClick, "aria-pressed": active, className: 'px-2.5 py-1 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ' +
            (active
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm font-medium'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'), children: children }));
}
// ----- Scheduled sentence: "Send this email on [date] at [time]." ----------
function ScheduledSentence({ value, onChange, }) {
    const local = toLocalParts(value.SendAtAbsolute);
    return (_jsxs("p", { className: "text-xl leading-relaxed text-slate-900 dark:text-slate-100", children: ["Send this email on", ' ', _jsx(DateToken, { value: local.date, onChange: d => onChange({ ...value, SendAtAbsolute: combineLocal(d, local.time) }) }), ' ', "at", ' ', _jsx(TimeToken, { value: local.time, onChange: t => onChange({ ...value, SendAtAbsolute: combineLocal(local.date, t) }) }), "."] }));
}
// ----- Event sentence: "Send [N] [unit] [before|after] [anchor]." ----------
function EventSentence({ value, onChange, anchorOptions, negativeAllowed, isLoading, }) {
    const offsetAbs = Math.abs(value.OffsetValue ?? 0);
    const direction = (value.OffsetValue ?? 0) < 0 ? 'before' : 'after';
    const unit = value.OffsetUnit ?? EmailOffsetUnit.Day;
    const isImmediate = offsetAbs === 0;
    if (isImmediate) {
        return (_jsxs("p", { className: "text-xl leading-relaxed text-slate-900 dark:text-slate-100", children: ["Send this email on", ' ', _jsx(AnchorToken, { value: value.EventType ?? null, options: anchorOptions, isLoading: isLoading, onChange: a => onChange({ ...value, EventType: a }) }), ".", ' ', _jsx("button", { type: "button", onClick: () => onChange({
                        ...value,
                        OffsetValue: negativeAllowed ? -1 : 1,
                        OffsetUnit: value.OffsetUnit ?? EmailOffsetUnit.Day,
                    }), className: "text-sm text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 underline decoration-dotted underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 rounded", children: "+ add offset" })] }));
    }
    return (_jsxs("p", { className: "text-xl leading-relaxed text-slate-900 dark:text-slate-100", children: ["Send this email", ' ', _jsx(NumberToken, { value: offsetAbs, min: 0, onChange: n => {
                    const signed = direction === 'before' ? -Math.abs(n) : Math.abs(n);
                    onChange({ ...value, OffsetValue: signed });
                } }), ' ', _jsx(UnitToken, { value: unit, plural: offsetAbs !== 1, onChange: u => onChange({ ...value, OffsetUnit: u }) }), ' ', _jsx(DirectionToken, { value: direction, negativeAllowed: negativeAllowed, onChange: d => {
                    const next = d === 'before' ? -Math.abs(value.OffsetValue ?? 0) : Math.abs(value.OffsetValue ?? 0);
                    onChange({ ...value, OffsetValue: next });
                } }), ' ', _jsx(AnchorToken, { value: value.EventType ?? null, options: anchorOptions, isLoading: isLoading, onChange: a => onChange({ ...value, EventType: a }) }), "."] }));
}
// ----- Tokens --------------------------------------------------------------
const TOKEN_BASE = 'appearance-none bg-transparent border-0 border-b-2 border-dotted ' +
    'border-slate-300 dark:border-slate-600 px-1 -mx-0.5 ' +
    'text-slate-900 dark:text-slate-100 font-medium ' +
    'hover:border-primary-500 hover:bg-primary-50/60 dark:hover:bg-primary-900/20 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ' +
    'rounded-sm transition-colors cursor-pointer';
function NumberToken({ value, min, onChange, }) {
    return (_jsx("input", { type: "number", value: value, min: min, step: 1, onInput: (e) => {
            const raw = e.target.value;
            const parsed = raw === '' ? 0 : Number.parseInt(raw, 10);
            if (Number.isNaN(parsed))
                return;
            onChange(Math.max(min, parsed));
        }, className: TOKEN_BASE + ' w-14 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none', "aria-label": "Offset value" }));
}
function UnitToken({ value, plural, onChange, }) {
    const label = (u) => (plural ? UNIT_PLURAL[u][1] : UNIT_PLURAL[u][0]);
    return (_jsxs("select", { value: value, onChange: (e) => onChange(e.target.value), className: TOKEN_BASE, "aria-label": "Unit", children: [_jsx("option", { value: EmailOffsetUnit.Minute, children: label(EmailOffsetUnit.Minute) }), _jsx("option", { value: EmailOffsetUnit.Hour, children: label(EmailOffsetUnit.Hour) }), _jsx("option", { value: EmailOffsetUnit.Day, children: label(EmailOffsetUnit.Day) })] }));
}
function DirectionToken({ value, negativeAllowed, onChange, }) {
    return (_jsxs("select", { value: value, onChange: (e) => onChange(e.target.value), className: TOKEN_BASE, "aria-label": "Direction", children: [_jsx("option", { value: "after", children: "after" }), _jsx("option", { value: "before", disabled: !negativeAllowed, children: "before" })] }));
}
function AnchorToken({ value, options, isLoading, onChange, }) {
    if (isLoading) {
        return _jsx("span", { className: "text-slate-400 dark:text-slate-500 italic", children: "loading\u2026" });
    }
    if (options.length === 0) {
        return _jsx("span", { className: "text-slate-400 dark:text-slate-500 italic", children: "no anchors available" });
    }
    return (_jsxs("select", { value: value ?? '', onChange: (e) => onChange(e.target.value), className: TOKEN_BASE, "aria-label": "Anchor", children: [!value && _jsx("option", { value: "", disabled: true, children: "pick an anchor" }), options.map(o => (_jsx("option", { value: o.key, children: o.label.toLowerCase() }, o.key)))] }));
}
function DateToken({ value, onChange }) {
    return (_jsx("input", { type: "date", value: value, onInput: (e) => onChange(e.target.value), className: TOKEN_BASE + ' [color-scheme:light] dark:[color-scheme:dark]', "aria-label": "Send date" }));
}
function TimeToken({ value, onChange }) {
    return (_jsx("input", { type: "time", value: value, onInput: (e) => onChange(e.target.value), className: TOKEN_BASE + ' [color-scheme:light] dark:[color-scheme:dark]', "aria-label": "Send time" }));
}
// ----- Date helpers --------------------------------------------------------
// Format a Date's local wall time as YYYY-MM-DD. Using toISOString() here would
// shift the date by a day in zones west of UTC after noon-ish.
function localDateString(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function defaultScheduledTime() {
    // Tomorrow at 09:00 local wall time.
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return `${localDateString(d)}T09:00:00`;
}
function toLocalParts(iso) {
    if (!iso) {
        return { date: localDateString(new Date()), time: '09:00' };
    }
    // Strip trailing Z / timezone for display; treat as wall time.
    const trimmed = iso.replace(/Z$/, '').slice(0, 16);
    const [datePart, timePart] = trimmed.split('T');
    return { date: datePart ?? '', time: (timePart ?? '09:00').slice(0, 5) };
}
function combineLocal(date, time) {
    return `${date}T${time}:00`;
}
